// A tune's PLAYBACK settings: everything that decides how a tune sounds, and
// nothing that decides how it reads (sizing, the view, the voicings and pins).
//
// Two layers carry them (docs/tune-library-plan.md §3, user ruling Sep 2026:
// "everything related to playback — not sizing or arrangement: band settings,
// key, tempo, the mix"):
//
//   - the HOUSE default the owner saves for everyone (Supabase `tune_defaults`,
//     cached on the device), and
//   - YOUR changes to a tune, remembered on this device.
//
// Opening a tune applies the house default and then your changes on top. Your
// changes are never overwritten; when the house default changes after you made
// yours, the tune offers the new one ("don't override unless we change
// something").
//
// Pure: no store import (the store imports this), so every guard here is
// testable on its own. Everything read back — a house row another account
// wrote, a record out of localStorage — goes through these guards field by
// field: a bad field is dropped, never allowed to reach the band.

import { isSwingFeel } from '@/music/swingFeel';
import { normalizeBandFeel, STRAIGHT_BAND_FEEL, SWING_BAND_FEEL } from '@/music/bandFeel';
import type { ArrangementPolicy } from '@/music/performance';
import { FEEL_KEYS, getStylePreset, type StyleBand } from '@/music/style';
import { normalizeSectionStarts, sectionLabel, type SectionStart } from '@/music/sectionPlan';
import type { PlaylistCall } from '@/storage/playlists';
import { COMP_STYLES } from '@/music/comping';
import { BASS_STYLES } from '@/music/walkingBass';
import { PIANO_COMP_FAMILIES } from '@/music/pianoComping';
import { PIANO_LEFT_HAND_FAMILIES } from '@/music/pianoLeftHand';
import { pitchClassOf } from '@/music/notes';
import { normalizeEnding, type EndingOptions } from '@/audio/endings';
import { normalizeLevels, type ChannelLevels } from '@/audio/levels';
import { normalizeCompressionAmount } from '@/audio/compression';
import type { BassSound, GuitarSound, LeadSound } from '@/audio/instrumentSamples';

/** The tempo every tempo write clamps to (store.ts re-exports these). */
export const TEMPO_MIN = 40;
export const TEMPO_MAX = 320;
/** The chorus count the Repeats panel offers. */
export const REPEATS_MAX = 16;

/**
 * The playback fields that are app state rather than score content: they
 * CARRY from tune to tune unless a layer sets them. When a layer does, the
 * value it replaced is remembered (`PlaybackDisplaced`) and put back when the
 * next tune opens, so one tune's house mix never leaks into the next.
 */
export const CARRY_KEYS = [
  'arrangementPolicy',
  'ending',
  'channelLevels',
  'masterReverb',
  'masterReverbEnabled',
  'masterCompression',
  'masterCompressionEnabled',
  'melodyChannel',
  'leadSound',
  'bassSound',
  'guitarSound',
] as const;
export type CarryKey = (typeof CARRY_KEYS)[number];

export interface CarrySettings {
  arrangementPolicy: ArrangementPolicy;
  ending: EndingOptions;
  channelLevels: ChannelLevels;
  masterReverb: number;
  masterReverbEnabled: boolean;
  masterCompression: number;
  masterCompressionEnabled: boolean;
  melodyChannel: boolean;
  leadSound: LeadSound;
  bassSound: BassSound;
  guitarSound: GuitarSound;
}

/** What a layer replaced in the carried fields, to put back on the next tune. */
export type PlaybackDisplaced = Partial<CarrySettings>;

/**
 * The settings that start from the TUNE itself: its written tempo, key and
 * chorus count. A layer moves them from there; nothing carries between tunes.
 * `key` is a tonic as the Key chip names it ("Bb", "F#"); the mode is the
 * tune's own.
 */
export interface TuneSideSettings {
  tempo: number;
  key: string;
  repeats: number;
}

/**
 * How a tune's SECTIONS play (sectionPlan.ts): a playback choice, never written
 * into the score. Sparse like the rest of the record: `starts` only when you
 * moved them (absent = the tune's detected sections), a band only for a
 * section you assigned one to. Off unless somebody turned it on.
 */
export interface TuneSections {
  on?: boolean;
  starts?: SectionStart[];
  /** A band per section label, each a whole copy (a section plays its band entire). */
  bands?: Record<string, StyleBand>;
  /** Whether each section plays the written melody (a pianist leaving the tune
   * to comp with both hands). A label left out plays it; only a stored value
   * says otherwise, so yours can turn back on what the house default rests. */
  melody?: Record<string, boolean>;
}

/**
 * YOUR changes to one tune (the band stays in `tuneBands`, the feel mapping in
 * `tuneFeels`, as before). Sparse: only what you changed, so a house default
 * change to anything you did not touch still reaches you. `basedOn` is the
 * house revision in force when you first changed something, or when you chose
 * to keep your version after the house one moved.
 */
export type TunePlaybackRecord = Partial<CarrySettings> &
  Partial<TuneSideSettings> & { basedOn?: number; sections?: TuneSections };

/** What the owner saves as the default for everyone. */
export interface HouseSettings extends Partial<CarrySettings>, Partial<TuneSideSettings> {
  band?: Partial<StyleBand>;
  /** The feel mapping, for tunes with written feel changes. */
  feels?: Record<string, StyleBand>;
  /** How the tune's sections play. */
  sections?: TuneSections;
}

/** One cached house default. */
export interface HouseDefault {
  tuneKey: string;
  title: string;
  composer: string;
  settings: HouseSettings;
  revision: number;
  updatedAt: string;
}

/** The written values of the tune on screen, captured at load, which Reset returns to. */
export interface WrittenPlayback {
  tempo: number;
  key: string | null;
  repeats: number;
}

/**
 * 2 (Oct 2026): written after the upright became the house bass and a
 * swinging band started walking with skips. A version 1 document recorded the
 * electric bass and `bassSkips: false` because they were the defaults when it
 * was saved (the capture takes every carried field), so it is read as if it
 * had not set them (`fromVersionOne`).
 */
export const HOUSE_SCHEMA_VERSION = 2;

// --- guards ---------------------------------------------------------------

const BAND_BOOLEANS = [
  'bassline',
  'bassSolo',
  'bassFollowForm',
  'bassSwing',
  'bassGhosts',
  'bassAnticipate',
  'bassTriplets',
  'drums',
] as const satisfies readonly (keyof StyleBand)[];
const BAND_UNITS = ['bassAmount', 'compAmount'] as const satisfies readonly (keyof StyleBand)[];

const BASS_STYLE_IDS: readonly string[] = BASS_STYLES.map((s) => s.id);
const COMP_STYLE_IDS: readonly string[] = COMP_STYLES.map((s) => s.id);
const PIANO_FAMILY_IDS: readonly string[] = [
  ...PIANO_COMP_FAMILIES.map((f) => f.id),
  ...PIANO_LEFT_HAND_FAMILIES.map((f) => f.id),
];
const COMP_INSTRUMENTS: readonly string[] = ['guitar', 'piano', 'piano-left'];
const LEAD_SOUNDS: readonly string[] = ['tenor-sax', 'trumpet', 'guitar', 'piano'];
const BASS_SOUNDS: readonly string[] = ['upright', 'electric', 'original'];
const GUITAR_SOUNDS: readonly string[] = ['jazz-guitar', 'original'];
const POLICIES: readonly string[] = ['as-written', 'head-solos-head'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function oneOf<T extends string>(value: unknown, allowed: readonly string[]): T | undefined {
  return typeof value === 'string' && allowed.includes(value) ? (value as T) : undefined;
}

function unit(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.max(0, Math.min(1, value))
    : undefined;
}

/** The valid band fields of an untrusted object; anything else is dropped. */
export function normalizeBandFields(raw: unknown): Partial<StyleBand> {
  if (!isRecord(raw)) return {};
  const out: Partial<StyleBand> = {};
  for (const key of BAND_BOOLEANS) {
    if (typeof raw[key] === 'boolean') out[key] = raw[key] as boolean;
  }
  for (const key of BAND_UNITS) {
    const value = unit(raw[key]);
    if (value !== undefined) out[key] = value;
  }
  const bassStyle = oneOf<StyleBand['bassStyle']>(raw.bassStyle, BASS_STYLE_IDS);
  if (bassStyle) out.bassStyle = bassStyle;
  const bassFeel = oneOf<StyleBand['bassFeel']>(raw.bassFeel, ['two', 'four']);
  if (bassFeel) out.bassFeel = bassFeel;
  if (isSwingFeel(raw.swingFeel)) out.swingFeel = raw.swingFeel;
  Object.assign(out, normalizeBandFeel(raw));
  const compStyle = oneOf<StyleBand['compStyle']>(raw.compStyle, COMP_STYLE_IDS);
  if (compStyle) out.compStyle = compStyle;
  const compInstrument = oneOf<StyleBand['compInstrument']>(raw.compInstrument, COMP_INSTRUMENTS);
  if (compInstrument) out.compInstrument = compInstrument;
  const compPianoFamily = oneOf<StyleBand['compPianoFamily']>(
    raw.compPianoFamily,
    PIANO_FAMILY_IDS,
  );
  if (compPianoFamily) out.compPianoFamily = compPianoFamily;
  if (
    typeof raw.drumLoopKey === 'string' &&
    raw.drumLoopKey.trim() &&
    raw.drumLoopKey.length <= 160
  ) {
    out.drumLoopKey = raw.drumLoopKey;
  }
  return out;
}

/**
 * A whole band from an untrusted object, `base` filling what it leaves out.
 * The feel fields a band stored before them lacks follow ITS OWN swing (a
 * stored bossa plays the straight feel), never the base's.
 */
export function bandFrom(base: StyleBand, raw: unknown): StyleBand {
  const fields = normalizeBandFields(raw);
  const swings = fields.bassSwing ?? base.bassSwing;
  return { ...base, ...(swings ? SWING_BAND_FEEL : STRAIGHT_BAND_FEEL), ...fields };
}

/** The valid carried fields of an untrusted object. */
export function normalizeCarry(raw: unknown): Partial<CarrySettings> {
  if (!isRecord(raw)) return {};
  const out: Partial<CarrySettings> = {};
  const policy = oneOf<ArrangementPolicy>(raw.arrangementPolicy, POLICIES);
  if (policy) out.arrangementPolicy = policy;
  if (isRecord(raw.ending)) out.ending = normalizeEnding(raw.ending);
  if (isRecord(raw.channelLevels)) out.channelLevels = normalizeLevels(raw.channelLevels);
  const reverb = unit(raw.masterReverb);
  if (reverb !== undefined) out.masterReverb = reverb;
  if (typeof raw.masterReverbEnabled === 'boolean')
    out.masterReverbEnabled = raw.masterReverbEnabled;
  if (typeof raw.masterCompression === 'number' && Number.isFinite(raw.masterCompression)) {
    out.masterCompression = normalizeCompressionAmount(raw.masterCompression);
  }
  if (typeof raw.masterCompressionEnabled === 'boolean') {
    out.masterCompressionEnabled = raw.masterCompressionEnabled;
  }
  if (typeof raw.melodyChannel === 'boolean') out.melodyChannel = raw.melodyChannel;
  const lead = oneOf<LeadSound>(raw.leadSound, LEAD_SOUNDS);
  if (lead) out.leadSound = lead;
  const bass = oneOf<BassSound>(raw.bassSound, BASS_SOUNDS);
  if (bass) out.bassSound = bass;
  const guitar = oneOf<GuitarSound>(raw.guitarSound, GUITAR_SOUNDS);
  if (guitar) out.guitarSound = guitar;
  return out;
}

/** A tonic the Key chip could name: a letter, at most one accidental. */
export function isTonic(value: unknown): value is string {
  if (typeof value !== 'string' || !/^[A-G][#b]?$/.test(value)) return false;
  try {
    pitchClassOf(value);
    return true;
  } catch {
    return false;
  }
}

/** The valid tune-side fields of an untrusted object. */
export function normalizeTuneSide(raw: unknown): Partial<TuneSideSettings> {
  if (!isRecord(raw)) return {};
  const out: Partial<TuneSideSettings> = {};
  if (typeof raw.tempo === 'number' && Number.isFinite(raw.tempo)) {
    out.tempo = Math.max(TEMPO_MIN, Math.min(TEMPO_MAX, Math.round(raw.tempo)));
  }
  if (isTonic(raw.key)) out.key = raw.key;
  if (typeof raw.repeats === 'number' && Number.isInteger(raw.repeats)) {
    out.repeats = Math.max(1, Math.min(REPEATS_MAX, raw.repeats));
  }
  return out;
}

/**
 * A sections record, guarded. Each band comes back WHOLE: `base` fills what a
 * stored band leaves out, the same rule as a feel's band. Undefined when
 * nothing valid is left.
 */
export function normalizeSections(
  raw: unknown,
  base: StyleBand = getStylePreset('swing')!.band,
): TuneSections | undefined {
  if (!isRecord(raw)) return undefined;
  const out: TuneSections = {};
  if (typeof raw.on === 'boolean') out.on = raw.on;
  const starts = normalizeSectionStarts(raw.starts);
  if (starts) out.starts = starts;
  if (isRecord(raw.bands)) {
    const bands: Record<string, StyleBand> = {};
    for (const [label, value] of Object.entries(raw.bands).slice(0, 64)) {
      const name = sectionLabel(label);
      if (!name || !isRecord(value)) continue;
      bands[name] = bandFrom(base, value);
    }
    if (Object.keys(bands).length) out.bands = bands;
  }
  if (isRecord(raw.melody)) {
    const melody: Record<string, boolean> = {};
    for (const [label, value] of Object.entries(raw.melody).slice(0, 64)) {
      const name = sectionLabel(label);
      if (name && typeof value === 'boolean') melody[name] = value;
    }
    if (Object.keys(melody).length) out.melody = melody;
  }
  return Object.keys(out).length ? out : undefined;
}

/**
 * Your sections over the house default's, field by field: the switch and the
 * starts are yours when you set them, and a band you assigned (or a melody
 * you turned on or off) wins for its section while the house's fill the rest.
 */
export function mergeSections(
  house: TuneSections | undefined,
  mine: TuneSections | undefined,
): TuneSections {
  const out: TuneSections = {};
  const on = mine?.on ?? house?.on;
  if (on !== undefined) out.on = on;
  const starts = mine?.starts ?? house?.starts;
  if (starts) out.starts = starts;
  const bands = { ...house?.bands, ...mine?.bands };
  if (Object.keys(bands).length) out.bands = bands;
  const melody = { ...house?.melody, ...mine?.melody };
  if (Object.keys(melody).length) out.melody = melody;
  return out;
}

/** Whether a section plays the written melody. */
export function sectionPlaysMelody(sections: TuneSections, label: string | null): boolean {
  return !label || sections.melody?.[label] !== false;
}

/** Whether a record changes anything (`basedOn` alone does not). */
export function recordHasSettings(record: TunePlaybackRecord | undefined): boolean {
  return !!record && Object.keys(record).some((key) => key !== 'basedOn');
}

/** One remembered record, guarded; undefined when nothing valid is left. */
export function normalizeTunePlaybackRecord(raw: unknown): TunePlaybackRecord | undefined {
  if (!isRecord(raw)) return undefined;
  const record: TunePlaybackRecord = { ...normalizeCarry(raw), ...normalizeTuneSide(raw) };
  const sections = normalizeSections(raw.sections);
  if (sections) record.sections = sections;
  if (typeof raw.basedOn === 'number' && Number.isInteger(raw.basedOn) && raw.basedOn >= 0) {
    record.basedOn = raw.basedOn;
  }
  return Object.keys(record).length ? record : undefined;
}

/** Every remembered record, guarded (the rehydrate path). */
export function normalizeTunePlayback(raw: unknown): Record<string, TunePlaybackRecord> {
  if (!isRecord(raw)) return {};
  const out: Record<string, TunePlaybackRecord> = {};
  for (const [key, entry] of Object.entries(raw)) {
    const record = normalizeTunePlaybackRecord(entry);
    if (record) out[key] = record;
  }
  return out;
}

/**
 * A house default's settings, guarded. Another account wrote these bytes, so a
 * document from a NEWER schema is refused whole (guessing at fields we do not
 * understand is how a default plays the wrong band), and within a known schema
 * each field stands or falls alone. A feel's band must come back whole, since
 * a feel plays its band entire; `base` fills what a stored band leaves out.
 */
export function normalizeHouseSettings(raw: unknown, base: StyleBand): HouseSettings | null {
  if (!isRecord(raw)) return null;
  const version = raw.schemaVersion;
  if (typeof version === 'number' && version > HOUSE_SCHEMA_VERSION) return null;
  const doc = version === 1 ? fromVersionOne(raw) : raw;
  const settings: HouseSettings = { ...normalizeCarry(doc), ...normalizeTuneSide(doc) };
  const band = normalizeBandFields(doc.band);
  if (Object.keys(band).length) settings.band = band;
  if (isRecord(doc.feels)) {
    const feels: Record<string, StyleBand> = {};
    for (const [label, value] of Object.entries(doc.feels)) {
      const name = label.trim();
      if (!name || name.length > 60 || !isRecord(value)) continue;
      feels[name] = bandFrom(base, value);
    }
    if (Object.keys(feels).length) settings.feels = feels;
  }
  const sections = normalizeSections(doc.sections, base);
  if (sections) settings.sections = sections;
  return settings;
}

/** A version 1 house document without the defaults it recorded only because
 * they were the defaults then: the electric bass, and a swinging band's
 * `bassSkips: false` (in its band, its feels and its sections). */
function fromVersionOne(raw: Record<string, unknown>): Record<string, unknown> {
  const skipping = (band: unknown): unknown => {
    if (!isRecord(band) || band.bassSwing !== true || band.bassSkips !== false) return band;
    const { bassSkips: _recorded, ...rest } = band;
    return rest;
  };
  const eachBand = (bands: unknown): unknown =>
    isRecord(bands)
      ? Object.fromEntries(Object.entries(bands).map(([key, band]) => [key, skipping(band)]))
      : bands;
  const { bassSound, ...rest } = raw;
  const out: Record<string, unknown> = { ...rest, band: skipping(raw.band) };
  if (bassSound !== 'electric') out.bassSound = bassSound;
  if (raw.feels !== undefined) out.feels = eachBand(raw.feels);
  if (isRecord(raw.sections))
    out.sections = { ...raw.sections, bands: eachBand(raw.sections.bands) };
  return out;
}

/** One cached house row, guarded; null drops it. */
export function normalizeHouseDefault(raw: unknown, base: StyleBand): HouseDefault | null {
  if (!isRecord(raw)) return null;
  const tuneKey = typeof raw.tuneKey === 'string' ? raw.tuneKey : '';
  if (!tuneKey || tuneKey.length > 300) return null;
  const settings = normalizeHouseSettings(raw.settings, base);
  if (!settings) return null;
  const revision =
    typeof raw.revision === 'number' && Number.isInteger(raw.revision) && raw.revision > 0
      ? raw.revision
      : 1;
  return {
    tuneKey,
    title: typeof raw.title === 'string' ? raw.title : '',
    composer: typeof raw.composer === 'string' ? raw.composer : '',
    settings,
    revision,
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : new Date(0).toISOString(),
  };
}

/** The whole cache, guarded (the rehydrate path). */
export function normalizeHouseDefaults(
  raw: unknown,
  base: StyleBand,
): Record<string, HouseDefault> {
  if (!isRecord(raw)) return {};
  const out: Record<string, HouseDefault> = {};
  for (const entry of Object.values(raw)) {
    const house = normalizeHouseDefault(entry, base);
    if (house) out[house.tuneKey] = house;
  }
  return out;
}

/** The carried fields out of any state object. */
export function pickCarry(state: CarrySettings): CarrySettings {
  const out = {} as Record<CarryKey, unknown>;
  for (const key of CARRY_KEYS) out[key] = state[key];
  return out as unknown as CarrySettings;
}

/**
 * Everything the owner saves, from the state on screen. Written with the
 * schema version so a later reader knows what it is reading.
 */
export function captureHouseSettings(
  state: CarrySettings & { tempo: number },
  tune: {
    band: StyleBand;
    key: string | null;
    repeats: number;
    feels?: Record<string, StyleBand>;
    sections?: TuneSections;
  },
): HouseSettings & { schemaVersion: number } {
  return {
    schemaVersion: HOUSE_SCHEMA_VERSION,
    band: { ...tune.band },
    ...(tune.feels && Object.keys(tune.feels).length ? { feels: structuredClone(tune.feels) } : {}),
    ...(tune.sections && Object.keys(tune.sections).length
      ? { sections: structuredClone(tune.sections) }
      : {}),
    tempo: Math.max(TEMPO_MIN, Math.min(TEMPO_MAX, Math.round(state.tempo))),
    ...(tune.key && isTonic(tune.key) ? { key: tune.key } : {}),
    repeats: Math.max(1, Math.min(REPEATS_MAX, Math.round(tune.repeats))),
    ...structuredClone(pickCarry(state)),
  };
}

/** Whether the house default moved after you made your changes. */
export function houseMovedSince(
  house: HouseDefault | undefined,
  record: TunePlaybackRecord | undefined,
): boolean {
  return !!house && house.revision > (record?.basedOn ?? 0);
}

// --- playlist calls ---------------------------------------------------------

/** The feel fields of a band: what a playlist's call carries of it. */
export function feelOf(band: Partial<StyleBand>): Partial<StyleBand> {
  const out: Partial<StyleBand> = {};
  for (const key of FEEL_KEYS) {
    if (band[key] !== undefined) (out as Record<string, unknown>)[key] = band[key];
  }
  return out;
}

/**
 * A playlist row's call, guarded. Other people write playlist rows (a shared
 * playlist is edited by everyone with the link), so each field stands or falls
 * alone, and a band field outside the feel is dropped: a call must never mute
 * somebody's drums or change who comps. Undefined when nothing valid is left.
 */
export function normalizePlaylistCall(raw: unknown): PlaylistCall | undefined {
  if (!isRecord(raw)) return undefined;
  const call: PlaylistCall = {};
  const side = normalizeTuneSide(raw);
  if (side.key) call.key = side.key;
  if (side.tempo !== undefined) call.tempo = side.tempo;
  if (side.repeats !== undefined) call.repeats = side.repeats;
  const band = feelOf(normalizeBandFields(raw.band));
  if (Object.keys(band).length) call.band = band;
  const policy = oneOf<ArrangementPolicy>(raw.policy, POLICIES);
  if (policy) call.policy = policy;
  if (isRecord(raw.ending)) call.ending = normalizeEnding(raw.ending);
  return Object.keys(call).length ? call : undefined;
}

/** The call a playlist would save from the state on screen. */
export function capturePlaylistCall(state: {
  tempo: number;
  band: StyleBand;
  key: string | null;
  repeats: number;
  arrangementPolicy: ArrangementPolicy;
  ending: EndingOptions;
}): PlaylistCall {
  return {
    ...(state.key && isTonic(state.key) ? { key: state.key } : {}),
    tempo: Math.max(TEMPO_MIN, Math.min(TEMPO_MAX, Math.round(state.tempo))),
    band: feelOf(normalizeBandFields(state.band)),
    repeats: Math.max(1, Math.min(REPEATS_MAX, Math.round(state.repeats))),
    policy: state.arrangementPolicy,
    ending: normalizeEnding(state.ending),
  };
}

function sortedJson(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortedJson);
  if (!isRecord(value)) return value;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(value).sort()) {
    if (value[key] !== undefined) out[key] = sortedJson(value[key]);
  }
  return out;
}

/** Whether two calls say the same thing, whatever order their fields came in. */
export function sameCall(a: PlaylistCall | undefined, b: PlaylistCall | undefined): boolean {
  return JSON.stringify(sortedJson(a ?? null)) === JSON.stringify(sortedJson(b ?? null));
}
